/**
 * Info client for read-only queries
 * Follows pattern from pkg/netrunner/clients for public/session auth operations
 * Automatically converts satoshi strings to decimals for display
 */

import { HttpClient } from "../../transport/http/HttpClient";
import { fromSatoshi } from "../../utils/math";
import { InfoClientConfig } from "../../types";

export class InfoClient {
  private httpClient: HttpClient;
  private sessionToken?: string;

  constructor(config: InfoClientConfig) {
    this.httpClient = new HttpClient(config.baseURL);
    this.sessionToken = config.sessionToken;
  }

  /**
   * Set session token for authenticated requests
   */
  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  /**
   * Get headers for requests
   * Follows pattern from DexClient.Do() for public/session auth
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json, */*",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    return headers;
  }

  // ============================================================================
  // MARKET DATA ENDPOINTS (Public)
  // ============================================================================

  /**
   * Get all markets
   * Uses HTTP endpoint /market/v1/all (matches Go client GetMarkets)
   */
  async getMarkets(): Promise<any> {
    const response = await this.httpClient.get(
      "/market/v1/all",
      this.getHeaders(),
    );
    return response.body?.markets ?? response.body;
  }

  /**
   * Get markets with filters
   * Uses HTTP endpoint /market/v1/all (matches Go client GetMarketsWithFilters)
   */
  async getMarketsWithFilters(
    filter?: string,
    activeOnly?: boolean,
  ): Promise<any> {
    let path = "/market/v1/all";
    const params: string[] = [];
    if (filter) {
      params.push(`filter=${filter}`);
    }
    if (activeOnly) {
      params.push("active_only=true");
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body?.markets ?? response.body;
  }

  /**
   * Get market by ID
   * Uses HTTP endpoint /market/v1/market/{marketId} (matches Go client GetMarketByID)
   */
  async getMarketByID(marketId: number | string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market by moniker symbol
   * Uses HTTP endpoint /market/v1/market/by-moniker/{moniker} (matches Go client GetMarketByMoniker)
   */
  async getMarketByMoniker(moniker: string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/market/by-moniker/${moniker}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market statistics (ticker data)
   * Uses HTTP endpoint /market/v1/market/{marketId}/stats (matches Go client GetMarketStats)
   */
  async getMarketStats(marketId: number | string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}/stats`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market ticker (alias for getMarketStats)
   * @deprecated Use getMarketStats instead
   */
  async getTicker(marketId: number | string): Promise<any> {
    return this.getMarketStats(marketId);
  }

  /**
   * Get exchange info
   * Uses HTTP endpoint /market/v1/exchangeInfo (matches Go client GetExchangeInfo)
   */
  async getExchangeInfo(): Promise<any> {
    const response = await this.httpClient.get(
      "/market/v1/exchangeInfo",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get orderbook for a market
   * Uses HTTP endpoint /clob/v1/orderbook/{marketId} (matches Go client GetOrderbook)
   */
  async getOrderbook(
    marketId: number | string,
    convertToDecimal: boolean = true,
    depth: number = 20,
  ): Promise<any> {
    let path = `/clob/v1/orderbook/${marketId}`;
    let depthValue = 20;
    if (depth > 0 && depth < 100) {
      depthValue = depth;
    }
    path += `?depth=${depthValue}`;

    const response = await this.httpClient.get(path, this.getHeaders());

    if (convertToDecimal && response.body) {
      if (response.body.bids && Array.isArray(response.body.bids)) {
        response.body.bids = response.body.bids.map(
          ([price, size]: [string | number, string | number]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
      if (response.body.asks && Array.isArray(response.body.asks)) {
        response.body.asks = response.body.asks.map(
          ([price, size]: [string | number, string | number]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
    }

    return response.body;
  }

  /**
   * Get market trades by market ID
   * Uses HTTP endpoint /clob/v1/trades?market_id={marketId} (matches Go client GetMarketTrades)
   */
  async getMarketTrades(
    marketId: number | string,
    limit?: number,
  ): Promise<any> {
    let path = `/clob/v1/trades?market_id=${marketId}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    console.debug(
      "[InfoClient] getMarketTrades response for marketId:",
      marketId,
      "response:",
      response.body,
    );

    // Normalize response to always return an array
    // Handle cases where API returns { trades: [...] }, { data: [...] }, or direct array
    if (Array.isArray(response.body)) {
      return response.body;
    }
    if (response.body?.trades && Array.isArray(response.body.trades)) {
      return response.body.trades;
    }
    if (response.body?.data && Array.isArray(response.body.data)) {
      return response.body.data;
    }

    console.warn(
      "[InfoClient] getMarketTrades: Unexpected response structure:",
      response.body,
    );
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get recent trades for a market (by market ID)
   * Uses HTTP endpoint /clob/v1/trades?market_id={marketId} (matches Go client GetMarketTrades)
   */
  async getRecentTrades(
    marketId: number | string,
    limit: number = 100,
  ): Promise<any> {
    return this.getMarketTrades(marketId, limit);
  }

  /**
   * Get trades by symbol
   * Uses HTTP endpoint /clob/v1/trades?symbol={symbol} (matches Go client GetTrades)
   */
  async getTrades(symbol: string, limit?: number): Promise<any> {
    let path = `/clob/v1/trades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get aggregate trades
   * Uses HTTP endpoint /market/v1/aggTrades (matches Go client GetAggTrades)
   */
  async getAggTrades(
    symbol: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/market/v1/aggTrades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get klines (candlestick data)
   * Uses HTTP endpoint /clob/v1/klines (matches Go client GetKlines)
   */
  async getKlines(
    symbol: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/clob/v1/klines?symbol=${symbol}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get continuous klines
   * Uses HTTP endpoint /market/v1/continuousKlines (matches Go client GetContinuousKlines)
   */
  async getContinuousKlines(
    pair: string,
    contractType: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/market/v1/continuousKlines?pair=${pair}&contractType=${contractType}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get premium index (mark price and funding rate)
   * Uses HTTP endpoint /market/v1/premiumIndex (matches Go client GetPremiumIndex)
   */
  async getPremiumIndex(symbol?: string): Promise<any> {
    let path = "/market/v1/premiumIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get funding rate history
   * Uses HTTP endpoint /market/v1/fundingRate (matches Go client GetFundingRate)
   */
  async getFundingRate(
    symbol?: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = "/market/v1/fundingRate";
    const params: string[] = [];
    if (symbol) {
      params.push(`symbol=${symbol}`);
    }
    if (limit && limit > 0) {
      params.push(`limit=${limit}`);
    }
    if (startTime && startTime > 0) {
      params.push(`startTime=${startTime}`);
    }
    if (endTime && endTime > 0) {
      params.push(`endTime=${endTime}`);
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get funding info
   * Uses HTTP endpoint /market/v1/fundingInfo (matches Go client GetFundingInfo)
   */
  async getFundingInfo(): Promise<any> {
    const response = await this.httpClient.get(
      "/market/v1/fundingInfo",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get delivery price
   * Uses HTTP endpoint /market/v1/deliveryPrice (matches Go client GetDeliveryPrice)
   */
  async getDeliveryPrice(pair: string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/deliveryPrice?pair=${pair}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get open interest
   * Uses HTTP endpoint /market/v1/openInterest (matches Go client GetOpenInterest)
   */
  async getOpenInterest(symbol: string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/openInterest?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get top long/short position ratio
   * Uses HTTP endpoint /market/v1/topLongShortPositionRatio (matches Go client GetTopLongShortPositionRatio)
   */
  async getTopLongShortPositionRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/market/v1/topLongShortPositionRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get global long/short account ratio
   * Uses HTTP endpoint /market/v1/globalLongShortAccountRatio (matches Go client GetGlobalLongShortAccountRatio)
   */
  async getGlobalLongShortAccountRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/market/v1/globalLongShortAccountRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get 24hr ticker statistics by symbol
   * Uses HTTP endpoint /market/v1/moniker/24hr (matches Go client GetTicker24hr)
   */
  async getTicker24hr(symbol?: string): Promise<any> {
    let path = "/market/v1/moniker/24hr";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get ticker price by symbol
   * Uses HTTP endpoint /market/v1/moniker/price (matches Go client GetTickerPrice)
   */
  async getTickerPrice(symbol?: string): Promise<any> {
    let path = "/market/v1/moniker/price";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get orderbook ticker by symbol
   * Uses HTTP endpoint /market/v1/moniker/bookTicker (matches Go client GetOrderbookMoniker)
   */
  async getOrderbookMoniker(symbol?: string): Promise<any> {
    let path = "/market/v1/moniker/bookTicker";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get basis data
   * Uses HTTP endpoint /market/v1/basis (matches Go client GetBasis)
   */
  async getBasis(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<any> {
    let path = `/market/v1/basis?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get index info
   * Uses HTTP endpoint /market/v1/indexInfo (matches Go client GetIndexInfo)
   */
  async getIndexInfo(symbol?: string): Promise<any> {
    let path = "/market/v1/indexInfo";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get asset index
   * Uses HTTP endpoint /market/v1/assetIndex (matches Go client GetAssetIndex)
   */
  async getAssetIndex(symbol?: string): Promise<any> {
    let path = "/market/v1/assetIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get constituents
   * Uses HTTP endpoint /market/v1/constituents (matches Go client GetConstituents)
   */
  async getConstituents(symbol: string): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/constituents?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get TWAP (time-weighted average price)
   * Uses HTTP endpoint /market/v1/twap (matches Go client GetTWAP)
   */
  async getTWAP(symbol: string, durationMinutes: number): Promise<any> {
    const response = await this.httpClient.get(
      `/market/v1/twap?symbol=${symbol}&duration=${durationMinutes}`,
      this.getHeaders(),
    );
    return response.body;
  }

  // ============================================================================
  // USER DATA ENDPOINTS (Session Auth Required)
  // ============================================================================

  /**
   * Get positions for authenticated user
   * Uses HTTP endpoint /bucket/v1/address/{address}/positions (matches Go client GetUserPositions)
   * Note: Requires walletAddress to be set or passed
   */
  async getPositions(
    walletAddress?: string,
    activeOnly?: boolean,
  ): Promise<any> {
    if (!walletAddress) {
      throw new Error("walletAddress is required for getPositions");
    }
    let path = `/bucket/v1/address/${walletAddress}/positions`;
    if (activeOnly !== undefined) {
      path += `?active_only=${activeOnly}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    // Normalize response to always return an array
    // Handle cases where API returns { positions: [...] } or direct array
    if (Array.isArray(response.body)) {
      return response.body;
    }
    if (response.body?.positions && Array.isArray(response.body.positions)) {
      return response.body.positions;
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get positions with decimal conversion for display
   */
  async getPositionsDecimal(
    walletAddress?: string,
    activeOnly?: boolean,
  ): Promise<any> {
    const positions = await this.getPositions(walletAddress, activeOnly);

    if (Array.isArray(positions)) {
      return positions.map((pos: any) => {
        // Handle both snake_case (from API) and camelCase field names
        const size = pos.size;
        const entryPrice = pos.entry_price || pos.entryPrice;
        const markPrice = pos.mark_price || pos.markPrice;
        const liquidationPrice = pos.liquidation_price || pos.liquidationPrice;
        const unrealizedPnL = pos.unrealized_pnl || pos.unrealizedPnL;

        return {
          ...pos,
          size: size ? fromSatoshi(size) : size,
          entry_price: entryPrice ? fromSatoshi(entryPrice) : entryPrice,
          entryPrice: entryPrice ? fromSatoshi(entryPrice) : entryPrice,
          mark_price: markPrice ? fromSatoshi(markPrice) : markPrice,
          markPrice: markPrice ? fromSatoshi(markPrice) : markPrice,
          liquidation_price: liquidationPrice
            ? fromSatoshi(liquidationPrice)
            : liquidationPrice,
          liquidationPrice: liquidationPrice
            ? fromSatoshi(liquidationPrice)
            : liquidationPrice,
          unrealized_pnl: unrealizedPnL
            ? fromSatoshi(unrealizedPnL)
            : unrealizedPnL,
          unrealizedPnL: unrealizedPnL
            ? fromSatoshi(unrealizedPnL)
            : unrealizedPnL,
        };
      });
    }

    return positions;
  }

  /**
   * Get balance for an address
   * Uses HTTP endpoint /bank/v1/balance/{address} (matches Go client GetBalance)
   */
  async getBalance(address: string, assetID: string): Promise<any> {
    let path = `/bank/v1/balance/${address}?asset=${assetID}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get balances for an address
   * Uses HTTP endpoint /bank/v1/balances/{address} (matches Go client GetBalances)
   */
  async getBalances(address: string): Promise<any> {
    const response = await this.httpClient.get(
      `/bank/v1/balances/${address}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get balance with decimal conversion
   */
  async getBalanceDecimal(address: string, assetID: string): Promise<any> {
    const balance = await this.getBalance(address, assetID);

    if (balance && typeof balance === "object") {
      const converted: Record<string, any> = { ...balance };
      if (converted.balance) {
        converted.balance = fromSatoshi(converted.balance);
      }
      if (converted.available) {
        converted.available = fromSatoshi(converted.available);
      }
      if (converted.locked) {
        converted.locked = fromSatoshi(converted.locked);
      }
      return converted;
    }

    return balance;
  }

  /**
   * Get orders for an address
   * Uses HTTP endpoint /clob/v1/address/orders (matches Go client GetOrders)
   * @param address - Wallet address to get orders for
   * @param assetPair - Optional asset pair ID to filter orders
   * @returns Response object with structure: { orders: Order[], total_count: number, timestamp: string }
   * @example
   * // Get all orders for an address
   * const result = await client.getOrders('0x3061750d3dF69ef7B8d4407CB7f3F879Fd9d2398');
   * // result.orders contains the array of orders
   * // result.total_count contains the total count
   *
   * // Get orders filtered by asset pair
   * const result = await client.getOrders('0x3061750d3dF69ef7B8d4407CB7f3F879Fd9d2398', '16210999351379558260');
   */
  async getOrders(address: string, assetPair?: string): Promise<any> {
    let path = `/clob/v1/address/orders?address=${encodeURIComponent(address)}`;
    if (assetPair) {
      path += `&asset_pair=${encodeURIComponent(assetPair)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get order by ID
   * Uses HTTP endpoint /clob/v1/order/{orderID} (matches Go client GetOrder)
   */
  async getOrder(orderID: string): Promise<any> {
    const response = await this.httpClient.get(
      `/clob/v1/order/${orderID}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get order history for an address
   * Uses HTTP endpoint /clob/v1/address/orders/history (matches Go client GetOrderHistory)
   */
  async getOrderHistory(address: string): Promise<any> {
    const response = await this.httpClient.get(
      `/clob/v1/address/orders/history?address=${address}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get user trades for an address
   * Uses HTTP endpoint /clob/v1/address/trades (matches Go client GetUserTrades)
   */
  async getUserTrades(address: string, assetPair?: string): Promise<any> {
    let path = `/clob/v1/address/trades?address=${address}`;
    if (assetPair) {
      path += `&asset_pair=${assetPair}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get trade history (alias for getUserTrades)
   */
  async getTradeHistory(address: string, assetPair?: string): Promise<any> {
    return this.getUserTrades(address, assetPair);
  }

  /**
   * Get position for an address
   * Uses HTTP endpoint /clob/v1/address/position (matches Go client GetPosition)
   */
  async getPosition(address: string, assetPair?: string): Promise<any> {
    let path = `/clob/v1/address/position?address=${address}`;
    if (assetPair) {
      path += `&asset_pair=${assetPair}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get buckets for a wallet address
   * Uses HTTP endpoint /bucket/v1/address/{address}/buckets (matches Go client GetBuckets)
   */
  async getBuckets(walletAddress: string): Promise<any> {
    const response = await this.httpClient.get(
      `/bucket/v1/address/${walletAddress}/buckets`,
      this.getHeaders(),
    );
    // Normalize response to always return an array
    // Handle cases where API returns { buckets: [...] } or direct array
    if (Array.isArray(response.body)) {
      return response.body;
    }
    if (response.body?.buckets && Array.isArray(response.body.buckets)) {
      return response.body.buckets;
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get bucket by ID
   * Uses HTTP endpoint /bucket/v1/bucket/{bucket_id} (matches Go client GetBucket)
   */
  async getBucket(bucketId: string): Promise<any> {
    const response = await this.httpClient.get(
      `/bucket/v1/bucket/${bucketId}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market data for asset pair
   * Uses HTTP endpoint /clob/v1/market/data (matches Go client GetMarketData)
   */
  async getMarketData(assetPair: string): Promise<any> {
    const response = await this.httpClient.get(
      `/clob/v1/market/data?asset_pair=${assetPair}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market candles
   * Uses HTTP endpoint /clob/v1/market/candles (matches Go client GetMarketCandles)
   */
  async getMarketCandles(
    assetPair: string,
    interval: string,
    limit: number,
  ): Promise<any> {
    const response = await this.httpClient.get(
      `/clob/v1/market/candles?asset_pair=${assetPair}&interval=${interval}&limit=${limit}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get nonce status for an address
   * Uses HTTP endpoint /router/v1/nonce/status/{address} (matches Go client GetNonceStatus)
   * @param address - Wallet address to query nonce for
   * @param reserve - Whether to reserve the nonce (default: false)
   * @returns Response with next_nonce field
   */
  async getNonceStatus(
    address: string,
    reserve: boolean = false,
  ): Promise<any> {
    let path = `/router/v1/nonce/status/${address}`;
    if (reserve) {
      path += "?reserve=true";
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  // ============================================================================
  // ROUTER V1 ENDPOINTS (Unified Write Service & Health)
  // ============================================================================

  /**
   * Get supported action types
   * Uses HTTP endpoint /router/v1/tx/supported-actions
   * @returns Array of supported action type strings
   */
  async getSupportedActions(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/tx/supported-actions",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Validate action before submission
   * Uses HTTP endpoint /router/v1/tx/validate-action
   * @param action - Action type to validate (e.g., "clob::submit_order")
   * @returns Validation result with valid flag
   */
  async validateAction(action: string): Promise<any> {
    const response = await this.httpClient.get(
      `/router/v1/tx/validate-action?action=${encodeURIComponent(action)}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get aggregated system health overview
   * Uses HTTP endpoint /router/v1/health/summary
   * @returns System health summary with overall status and component health
   */
  async getHealthSummary(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/health/summary",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get detailed daemon status (relayer/mailbox)
   * Uses HTTP endpoint /router/v1/health/daemons
   * @returns Detailed daemon status information
   */
  async getHealthDaemons(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/health/daemons",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get risk engine health status
   * Uses HTTP endpoint /router/v1/health/bucket
   * @returns Risk engine (bucket) health status
   */
  async getHealthBucket(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/health/bucket",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get oracle connectivity health
   * Uses HTTP endpoint /router/v1/health/oracle
   * @returns Oracle connection status and pool information
   */
  async getHealthOracle(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/health/oracle",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get server time in milliseconds (Binance-compatible)
   * Uses HTTP endpoint /router/v1/time
   * @returns Server time in milliseconds (Unix timestamp)
   */
  async getServerTime(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/time",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get transactions per second metrics
   * Uses HTTP endpoint /router/v1/metrics/tps
   * @returns TPS statistics including current, peak, and averages
   */
  async getMetricsTPS(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/tps",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get latency statistics (P50, P95, P99, Avg, Max, Min)
   * Uses HTTP endpoint /router/v1/metrics/latency
   * @returns Latency percentiles and statistics
   */
  async getMetricsLatency(): Promise<any> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/latency",
      this.getHeaders(),
    );
    return response.body;
  }
}
